
import React, { useEffect, useState } from 'react';
import { Form, Button, DatePicker } from 'antd';
import CircularProgress from '@mui/material/CircularProgress';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import PopupNotification from './PopupNotification';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import './auditCreation.css';
import './createAudit.css';
import {
  StyledInput,
  StyledSelect,
} from '../login-module/customComponents/styled_input';
import axios from 'axios';
import StepIndicator from './inspection-mode/StepIndicator';

const ClaimNewAudit = () => {
  const theme = createTheme({
    components: {
      MuiInputBase: { styleOverrides: { root: { fontSize: '16px' } } },
    },
  });
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoadingState] = useState(false);

  const [popupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');

  const [userId, setUserId] = useState(null);
  const [claimId, setClaimId] = useState('');

  // ✅ No prefill: everything empty initially (except auto-generated ids)
  const [caseDetails, setCaseDetails] = useState({
    'User Id': '',
    'Claim Id': '',
    'Owner Name': '',
    'Adhaar No.': '',
    'Ear Tag No': '',
    Breed: '',
    'Date & Time of Death': null,
    'Sum Insured': '',
    'MPCS Name': '',
    Taluk: '',
    District: '',
    'Attending Doctor': '',
    'Doctor Phone No': '',
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const showPopup = (msg) => {
    setPopupMessage(msg);
    setPopupVisible(true);
  };

  const generateRandomNDigitString = (n) => {
    const uuid = uuidv4().replace(/-/g, '');
    return uuid.substring(0, n).toUpperCase();
  };

  // ✅ same generateRandomId style you shared (12 chars: 4 random + DDHHMMSS)
  const generateRandomId = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 4; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const mins = now.getMinutes().toString().padStart(2, '0');
    const secs = now.getSeconds().toString().padStart(2, '0');
    const dateTimePart = day + hours + mins + secs;
    result += dateTimePart;
    return result.slice(0, 12);
  };

  // ✅ generate userId once
  useEffect(() => {
    const uuid = generateRandomNDigitString(10);
    setUserId(uuid);
  }, []);

  // ✅ generate claimId once (on first load)
  useEffect(() => {
    const cid = generateRandomId();
    setClaimId(cid);
  }, []);

  // ✅ set initial ids into state + form (still not "prefill" from external data)
  useEffect(() => {
    if (!userId || !claimId) return;

    setCaseDetails((prev) => ({
      ...prev,
      'User Id': userId,
      'Claim Id': claimId,
    }));

    form.setFieldsValue({
      'User Id': userId,
      'Claim Id': claimId,
    });
  }, [userId, claimId, form]);

  const handleInputChange = (key, eOrValue) => {
    const value = eOrValue?.target !== undefined ? eOrValue.target.value : eOrValue;
    setCaseDetails((prev) => ({ ...prev, [key]: value }));
    form.setFieldsValue({ [key]: value });
  };

  const handleSelectionChange = (key, value) => {
    setCaseDetails((prev) => ({ ...prev, [key]: value }));
    form.setFieldsValue({ [key]: value });
  };

  const handleDateTimeChange = (value) => {
    setCaseDetails((prev) => ({ ...prev, 'Date & Time of Death': value }));
    form.setFieldsValue({ 'Date & Time of Death': value });
  };

  const validateForm = () => {
    const required = [
      'Claim Id',
      'Owner Name',
      'Ear Tag No',
      'Breed',
      'Date & Time of Death',
      'Sum Insured',
      'MPCS Name',
      'Taluk',
      'District',
      'Attending Doctor',
      'Doctor Phone No',
    ];

    return required.every((f) => {
      const v = caseDetails[f];
      if (v === null || v === undefined) return false;
      if (dayjs.isDayjs(v)) return true;
      return v.toString().trim() !== '';
    });
  };

  useEffect(() => {
    setIsFormValid(validateForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDetails]);


  const onFinish = async () => {
    const phone = (caseDetails['Doctor Phone No'] ?? '').toString().trim();
    if (phone.length !== 10 || !/^\d+$/.test(phone)) {
      showPopup("Doctor phone number must be exactly 10 digits");
      return;
    }

    const sum = (caseDetails['Sum Insured'] ?? '').toString().trim();
    if (!sum || Number.isNaN(Number(sum)) || Number(sum) <= 0) {
      showPopup('Sum insured must be a valid positive number');
      return;
    }
    setLoadingState(true);

    const insuranceConfig = JSON.parse(localStorage.getItem("inspectionConfig") || "{}");
    const insuranceUser = JSON.parse(localStorage.getItem("insuranceUser") || "{}");

    const user_Id = insuranceUser?.userId;
    const configData = insuranceConfig?.inspection_config;
    const orgId = insuranceConfig?.org_id;
    console.log(caseDetails["Claim Id"], "caseDetails")
    try {
      // 1) create token row in claimInsurance table
      await axios.post("/create_audit/claim_insurance_token", {
        claimID: caseDetails["Claim Id"], // generated via generateRandomId()
        lead_creation_time: moment().format("DD-MM-YYYY HH:mm"),
        user_ID: user_Id,
        configData,
        orgId,
      });

      // 2) then upload/store the death-intimation form data (your existing flow)
      // await axios.post("/claim_insurance/save_death_intimation", { ...mapped fields... });
      // 2) save death intimation form fields
      await axios.post("/create_audit/save_death_intimation", {
        claim_id: caseDetails["Claim Id"],
        user_id: user_Id,
        owner_name: caseDetails["Owner Name"],
        adhaar_no: caseDetails["Adhaar No."],
        ear_tag_no: caseDetails["Ear Tag No"],
        breed: caseDetails["Breed"],
        death_datetime: caseDetails["Date & Time of Death"]
          ? dayjs(caseDetails["Date & Time of Death"]).format("YYYY-MM-DD HH:mm:ss")
          : null,
        sum_insured: Number(caseDetails["Sum Insured"] || 0),
        mpcs_name: caseDetails["MPCS Name"],
        taluk: caseDetails["Taluk"],
        district: caseDetails["District"],
        attending_doctor: caseDetails["Attending Doctor"],
        doctor_phone: caseDetails["Doctor Phone No"],
      });

      showPopup("Death intimation submitted successfully ✅");
      console.log('All uploads successful');
      navigate('/auditList');
      console.log("Claim token created + claim data can be submitted next ✅");
    } catch (e) {
      console.error(e);
      showPopup("Failed to create claim token. Please try again.");
    } finally {
      setLoadingState(false);
    }
  };
  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          marginTop: "20px",
          overflow: "hidden",
        }}
      >
        <div
          className="responsive-form-container"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            paddingBottom: "80px",
          }}
        >
          {loading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress size={40} sx={{ color: '#FC350B' }} />
            </div>
          )}

          <PopupNotification
            visible={popupVisible}
            message={popupMessage}
            onClose={() => setPopupVisible(false)}
          />
          <div style={styles.caseDetails}>Death Intimation</div>
          {/* <div style={{marginTop : "-70px"}}>
          <StepIndicator stepNumber={1} lineWidth="200px" /></div> */}

          <div className="form-data">
            <div className="audit-details-group" style={{ width: '80vw' }}>

              {/* Claim Id (auto-generated) */}
              <div className="input-wrapper">
                <Form.Item name="Claim Id" rules={[{ required: true, message: 'Claim Id is required' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Claim Id']}
                      style={{ width: 200 }}
                      placeholder=" "
                      disabled
                    />
                    <span className={caseDetails['Claim Id'] ? 'fill' : 'unfilled'}>Claim Id</span>
                  </div>
                </Form.Item>
              </div>

              {/* Owner Name */}
              <div className="input-wrapper">
                <Form.Item name="Owner Name" rules={[{ required: true, message: 'Please enter Owner Name' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Owner Name']}
                      style={{ width: 200 }}
                      placeholder=" "
                      onChange={(e) => handleInputChange('Owner Name', e)}
                    />
                    <span className={caseDetails['Owner Name'] ? 'fill' : 'unfilled'}>Owner Name</span>
                  </div>
                </Form.Item>
              </div>

              <div className="input-wrapper">
                <Form.Item name="Adhaar No." rules={[{ required: true, message: 'Adhaar No. is required' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Adhaar No.']}
                      style={{ width: 200 }}
                      placeholder=" "
                      maxLength={12}
                      onChange={(e) => handleInputChange('Adhaar No.', e)}
                    />
                    <span className={caseDetails['Adhaar No.'] ? 'fill' : 'unfilled'}>Adhaar No.</span>
                  </div>
                </Form.Item>
              </div>
              {/* Ear Tag No */}
              <div className="input-wrapper">
                <Form.Item name="Ear Tag No" rules={[{ required: true, message: 'Please enter Ear Tag No' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Ear Tag No']}
                      style={{ width: 200 }}
                      placeholder=" "
                      maxLength={20}
                      onChange={(e) => handleInputChange('Ear Tag No', e)}
                    />
                    <span className={caseDetails['Ear Tag No'] ? 'fill' : 'unfilled'}>Ear Tag No</span>
                  </div>
                </Form.Item>
              </div>

              {/* Breed */}
              <div className="input-wrapper">
                <Form.Item name="Breed" rules={[{ required: true, message: 'Please select Breed' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledSelect
                      value={caseDetails['Breed']}
                      style={{ width: 200 }}
                      placeholder=" "
                      onChange={(v) => handleSelectionChange('Breed', v)}
                      options={[
                        { value: 'Hfx', label: 'Hfx' },
                        { value: 'HF', label: 'HF' },
                        { value: 'Jersey', label: 'Jersey' },
                        { value: 'Local', label: 'Local' },
                        { value: 'Other', label: 'Other' },
                      ]}
                    />
                    <span className={caseDetails['Breed'] ? 'fill' : 'unfilled'}>Breed</span>
                  </div>
                </Form.Item>
              </div>

              {/* Date & Time of Death */}
              <div className="input-wrapper">
                <Form.Item
                  name="Date & Time of Death"
                  rules={[{ required: true, message: 'Please select Date & Time of Death' }]}
                >
                  <div style={{ position: 'relative' }}>
                    <DatePicker
                      className="ant-input"
                      style={{ width: 200 }}
                      placeholder=" "
                      format="DD-MM-YYYY"
                      // showTime={{ use12Hours: true, format: 'hh:mm A' }}
                      value={caseDetails['Date & Time of Death']}
                      onChange={handleDateTimeChange}
                    />
                    <span className={caseDetails['Date & Time of Death'] ? 'fill' : 'unfilled'}>
                      Date & Time of Death
                    </span>
                  </div>
                </Form.Item>
              </div>

              {/* Sum Insured */}
              <div className="input-wrapper">
                <Form.Item name="Sum Insured" rules={[{ required: true, message: 'Please enter Sum Insured' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Sum Insured']}
                      style={{ width: 200 }}
                      placeholder=" "
                      onChange={(e) => handleInputChange('Sum Insured', e)}
                    />
                    <span className={caseDetails['Sum Insured'] ? 'fill' : 'unfilled'}>Sum Insured</span>
                  </div>
                </Form.Item>
              </div>

              {/* MPCS Name */}
              <div className="input-wrapper">
                <Form.Item name="MPCS Name" rules={[{ required: true, message: 'Please enter MPCS Name' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['MPCS Name']}
                      style={{ width: 200 }}
                      placeholder=" "
                      onChange={(e) => handleInputChange('MPCS Name', e)}
                    />
                    <span className={caseDetails['MPCS Name'] ? 'fill' : 'unfilled'}>MPCS Name</span>
                  </div>
                </Form.Item>
              </div>

              {/* Taluk */}
              <div className="input-wrapper">
                <Form.Item name="Taluk" rules={[{ required: true, message: 'Please enter Taluk' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Taluk']}
                      style={{ width: 200 }}
                      placeholder=" "
                      onChange={(e) => handleInputChange('Taluk', e)}
                    />
                    <span className={caseDetails['Taluk'] ? 'fill' : 'unfilled'}>Taluk</span>
                  </div>
                </Form.Item>
              </div>

              {/* District */}
              <div className="input-wrapper">
                <Form.Item name="District" rules={[{ required: true, message: 'Please enter District' }]}>
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['District']}
                      style={{ width: 200 }}
                      placeholder=" "
                      onChange={(e) => handleInputChange('District', e)}
                    />
                    <span className={caseDetails['District'] ? 'fill' : 'unfilled'}>District</span>
                  </div>
                </Form.Item>
              </div>

              {/* Attending Doctor */}
              <div className="input-wrapper">
                <Form.Item
                  name="Attending Doctor"
                  rules={[{ required: true, message: 'Please enter Attending Doctor' }]}
                >
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Attending Doctor']}
                      style={{ width: 200 }}
                      placeholder=" "
                      onChange={(e) => handleInputChange('Attending Doctor', e)}
                    />
                    <span className={caseDetails['Attending Doctor'] ? 'fill' : 'unfilled'}>
                      Attending Doctor
                    </span>
                  </div>
                </Form.Item>
              </div>

              {/* Doctor Phone No */}
              <div className="input-wrapper">
                <Form.Item
                  name="Doctor Phone No"
                  rules={[
                    { required: true, message: 'Please enter Doctor Phone No' },
                    { len: 10, message: 'Phone must be 10 digits' },
                  ]}
                >
                  <div style={{ position: 'relative' }}>
                    <StyledInput
                      value={caseDetails['Doctor Phone No']}
                      style={{ width: 200 }}
                      placeholder=" "
                      maxLength={10}
                      onChange={(e) =>
                        handleInputChange('Doctor Phone No', (e.target.value || '').replace(/\D/g, ''))
                      }
                    />
                    <span className={caseDetails['Doctor Phone No'] ? 'fill' : 'unfilled'}>
                      Doctor Phone No
                    </span>
                  </div>
                </Form.Item>
              </div>
            </div>

            <div
              style={{
                padding: "16px",
                textAlign: "center",
                flexShrink: 0,
              }}
            >
              <Form form={form} onFinish={onFinish}>
                <Form.Item style={{ margin: '0px' }}>
                  <Button
                    style={{
                      backgroundColor: isFormValid ? '#FC350B' : 'gray',
                      color: '#FFFFFF',
                      minHeight: '40px',
                      width: '300px',
                      borderRadius: '2px',
                    }}
                    htmlType="submit"
                    className="responsive-button"
                    disabled={!isFormValid || loading}
                  >
                    Submit Death Intimation
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>

        {/* If you use ToastContainer in your project, add it here */}
        {/* <ToastContainer /> */}
      </div>
    </ThemeProvider>
  );
};
const styles = {
  caseDetails: {
    marginTop: "56px",
    marginBottom: "19px",
    fontFamily:
      '"Barlow Condensed", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    fontWeight: 600,
    fontSize: "17px",
    lineHeight: "75%",
    letterSpacing: "0.25px",
    textAlign: "center",
    color: "#000000DE",
  },
};

export default ClaimNewAudit;
